/**
 * Crisp loads on the visitor's own click, or once they have granted analytics or marketing.
 * It is never loaded on page view for a visitor with no choice (spec section 2.1, option 1).
 * Mirrors apps/blueticks/src/lib/consent/crisp.ts on blueticks.co.
 */
import { appConfig } from "@common-config/app.config";

// Window.$crisp is declared in crisp-service.ts; read it through a loose view here.
type CrispWindow = { $crisp?: unknown[]; CRISP_WEBSITE_ID?: string };
const w = (): CrispWindow => window as unknown as CrispWindow;

let crispPromise: Promise<void> | null = null;

/** Loads l.js once. Resolves when the script has executed. */
export function loadCrisp(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("crisp: no DOM"));
  }
  if (crispPromise) return crispPromise;
  crispPromise = new Promise<void>((resolve, reject) => {
    w().$crisp = w().$crisp || [];
    w().CRISP_WEBSITE_ID = appConfig.CRISP_ID;
    const s = document.createElement("script");
    s.src = "https://client.crisp.chat/l.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      crispPromise = null;
      s.remove();
      reject(new Error("crisp: failed to load"));
    };
    document.head.appendChild(s);
  });
  return crispPromise;
}

/** Loads Crisp if needed and opens the chat. Resolves false if Crisp could not load (blocked). */
export async function openCrispChat(): Promise<boolean> {
  try {
    await loadCrisp();
    const queue = w().$crisp;
    if (!queue) return false;
    queue.push(["do", "chat:show"]);
    queue.push(["do", "chat:open"]);
    return true;
  } catch {
    return false;
  }
}
